import { FlexBox } from "@common/FlexBox";
import {
  // CategoryOutlined,
  // ChecklistOutlined,
  FolderOpenOutlined,
  Inventory2Outlined,
  InventoryOutlined,
  LocationOn,
  AssignmentOutlined,
  SpaceDashboardOutlined,
  PeopleOutlined,
  LocalShippingOutlined,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  styled,
  useTheme,
  Typography,
  Tooltip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { MobileMenu } from "./MobileMenu";
import { SettingsPopup } from "./SettingsPopup";
import { useEffect, useState } from "react";
import { useOrganizationStore } from "@stores/organizationStore";

const MenuButton = styled(IconButton)<{ isactive?: string }>(
  ({ theme, isactive }) => ({
    borderRadius: "8px",
    padding: "0px",
    width: "32px",
    height: "32px",
    minWidth: "32px",
    transition: "all 0.2s ease-in-out",
    color:
      isactive === "true"
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
    ".menu-item-container:hover &": {
      color: theme.palette.text.primary,
    },
  })
);

const MenuItemContainer = styled(Box)<{ isactive?: string }>(
  ({ theme, isactive }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    padding: "0 4px",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s ease-in-out",
    ...(isactive === "true" && {
      backgroundColor: theme.palette.action.hover,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  })
);

const LogoText = styled(Typography)(() => ({
  fontSize: "18px",
  fontWeight: 700,
  whiteSpace: "nowrap",
  opacity: 0,
  width: 0,
  overflow: "hidden",
  transition: "opacity 0.2s ease-in-out, width 0.2s ease-in-out",
  ".side-menu:hover &": {
    opacity: 1,
    width: "auto",
  },
}));

const MenuText = styled(Typography)<{ isactive?: string }>(
  ({ theme, isactive }) => ({
    fontSize: "14px",
    fontWeight: 500,
    color:
      isactive === "true"
        ? theme.palette.text.primary
        : theme.palette.text.secondary,
    whiteSpace: "nowrap",
    opacity: 0,
    width: 0,
    overflow: "hidden",
    transition:
      "opacity 0.2s ease-in-out, width 0.2s ease-in-out, color 0.2s ease-in-out",
    ".side-menu:hover &": {
      opacity: 1,
      width: "auto",
    },
    ".menu-item-container:hover &": {
      color: theme.palette.text.primary,
    },
  })
);

// Should be configurable

const menuConfig = [
  {
    text: "Dashboard",
    icon: <SpaceDashboardOutlined />,
    path: "/dashboard",
    isMobile: true,
    mobilePrimary: false,
    requiresOrgMember: false,
  },
  {
    text: "Products",
    icon: <Inventory2Outlined />,
    path: "/products/all-products",
    isMobile: true,
    mobilePrimary: true,
    requiresOrgMember: false,
  },
  {
    text: "Locations",
    icon: <LocationOn />,
    path: "/locations/all-locations",
    isMobile: true,
    mobilePrimary: false,
    requiresOrgMember: false,
  },
  {
    text: "Inventory",
    icon: <FolderOpenOutlined />,
    path: "/inventory",
    isMobile: true,
    mobilePrimary: false,
    requiresOrgMember: false,
  },
  {
    text: "Stocks",
    icon: <InventoryOutlined />,
    path: "/stocks/all-stocks",
    isMobile: true,
    mobilePrimary: true,
    requiresOrgMember: false,
  },
  // { text: 'Categories', icon: <CategoryOutlined />, path: '/categories' },
  // { text: 'Logs', icon: <ChecklistOutlined />, path: '/logs' },
  {
    text: "Orders",
    icon: <AssignmentOutlined />,
    path: "/orders/all-orders",
    isMobile: true,
    mobilePrimary: true,
    requiresOrgMember: true,
  },
  {
    text: "Customers",
    icon: <PeopleOutlined />,
    path: "/customers/all-customers",
    isMobile: true,
    mobilePrimary: false,
    requiresOrgMember: true,
  },
  {
    text: "Suppliers",
    icon: <LocalShippingOutlined />,
    path: "/suppliers/all-suppliers",
    isMobile: true,
    mobilePrimary: false,
    requiresOrgMember: true,
  },
  {
    text: "Purchase Orders",
    icon: <ShoppingCartOutlined />,
    path: "/purchase-orders/all-purchase-orders",
    isMobile: true,
    mobilePrimary: false,
    requiresOrgMember: true,
  },
];

export function MainMenu() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeRoute, setActiveRoute] = useState("");
  const location = useLocation();
  const { isOrganizationMember } = useOrganizationStore();

  useEffect(() => {
    const path = location.pathname === "/" ? "/products" : location.pathname;
    setActiveRoute(path);
  }, [location.pathname]);

  return (
    <>
      <MobileMenu
        menuConfig={menuConfig}
        isOrganizationMember={isOrganizationMember}
      />

      <SideMenu className="side-menu">
        <FlexBox
          vertical
          spaceBetween
          style={{
            height: "100%",
            paddingBottom: 12,
            alignItems: "stretch",
            paddingTop: 10,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              alignItems: "stretch",
              width: "100%",
              px: 0.5,
            }}
          >
            {/* Logo container */}
            <Box
              onClick={() => {
                navigate("/dashboard");
                setActiveRoute("/dashboard");
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                padding: "4px",
                borderRadius: 1,
                alignSelf: "center",
                "&:hover": {
                  opacity: 0.8,
                  transition: "opacity 0.2s ease-in-out",
                },
              }}
            >
              <Box
                component="img"
                src="/pwa-192x192.png"
                alt="Skuey Logo"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  objectFit: "cover",
                }}
              />
              <LogoText className="logo-text">
                <span
                  style={{
                    paddingLeft: 4,
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.text.primary
                        : "#2C2C2C",
                  }}
                >
                  Sku
                </span>
                <span style={{ color: "#3BABFF" }}>ey</span>
              </LogoText>
            </Box>

            {menuConfig.map((config) => {
              const isRouteActive = config.path === activeRoute;
              const isDisabled =
                config.requiresOrgMember && !isOrganizationMember;

              const menuItem = (
                <MenuItemContainer
                  key={config.text}
                  className="menu-item-container"
                  isactive={isRouteActive ? "true" : "false"}
                  onClick={() => {
                    if (!isDisabled) {
                      navigate(config.path);
                      setActiveRoute(config.path);
                    }
                  }}
                  sx={{
                    ...(isDisabled && {
                      opacity: 0.5,
                      cursor: "not-allowed",
                    }),
                  }}
                >
                  <MenuButton isactive={isRouteActive ? "true" : "false"}>
                    {config.icon}
                  </MenuButton>
                  <MenuText isactive={isRouteActive ? "true" : "false"}>
                    {config.text}
                  </MenuText>
                </MenuItemContainer>
              );

              if (isDisabled) {
                return (
                  <Tooltip
                    key={config.text}
                    title={
                      <>
                        You need to be part of an organization to use these
                        features.
                        <br />
                        To join or create an organization, please go to
                        Settings.
                      </>
                    }
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: "14px",
                        },
                      },
                    }}
                  >
                    <Box sx={{ width: "100%" }}>{menuItem}</Box>
                  </Tooltip>
                );
              }

              return menuItem;
            })}
          </Box>
          <SettingsPopup />
        </FlexBox>
      </SideMenu>
    </>
  );
}

const SideMenu = styled("div")(({ theme }) => ({
  borderRight: `.5px solid  ${theme.palette.divider} `,
  justifyContent: "space-between",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  paddingBottom: 2,
  width: 47,
  alignItems: "stretch",
  position: "fixed",
  left: 0,
  top: 0,
  zIndex: 1000,
  backgroundColor: theme.palette.background.default,
  transition: "width 0.2s ease-in-out",
  overflow: "hidden",
  "&:hover": {
    width: 200,
    boxShadow: theme.shadows[4],
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
  "@media (max-width: 768px)": {
    display: "none",
  },
}));
